import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Users, QrCode, Shield, ArrowRight, Check, ChevronRight, Smartphone, BarChart3, Mail, Globe, Zap, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import usePageTitle from "@/hooks/usePageTitle";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: CalendarDays, title: "Creation d'evenements", desc: "Creez et gerez vos evenements en quelques clics. Titre, dates, lieu, capacite, tout est personnalisable." },
  { icon: Users, title: "Inscriptions en ligne", desc: "Formulaire personnalisable. Collectez les informations dont vous avez besoin pour chaque participant." },
  { icon: QrCode, title: "QR Codes securises", desc: "Chaque participant recoit un QR Code unique. Controle d'acces rapide et fiable a l'entree." },
  { icon: Smartphone, title: "Scanner mobile", desc: "Scannez les QR Codes depuis un smartphone. Validation instantanee avec indicateur visuel vert/orange/rouge." },
  { icon: BarChart3, title: "Statistiques temps reel", desc: "Suivez en direct le nombre d'inscrits, de presents, le taux de participation et les categories." },
  { icon: Mail, title: "Communications", desc: "Envoyez confirmations, rappels et notifications a vos participants directement depuis la plateforme." },
];

const STEPS_DATA = [
  { num: 1, title: "Organisation", fields: ["org_name", "responsible_name", "phone", "email", "website", "country", "city"] },
  { num: 2, title: "Besoin", fields: ["event_type", "estimated_participants", "planned_date", "event_frequency", "needs"] },
  { num: 3, title: "Personnalisation", fields: ["primary_color", "platform_name", "additional_info"] },
  { num: 4, title: "Validation", fields: [] },
];

export default function EventLandingPage() {
  usePageTitle("GLOTECH Event Center");
  const [showSignup, setShowSignup] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    org_name: "", responsible_name: "", phone: "", email: "", website: "", country: "", city: "",
    event_type: "", estimated_participants: "", planned_date: "", event_frequency: "",
    need_qrcode: true, need_access_control: false, need_badges: false, need_audiovisual: false, need_photo_video: false,
    primary_color: "#0099CC", secondary_color: "#10B981", platform_name: "", additional_info: "",
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { ...form, estimated_participants: parseInt(form.estimated_participants) || 0 };
      await axios.post(`${API}/api/event/signup`, payload);
      setSubmitted(true);
      toast.success("Demande envoyee avec succes !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const canNext = () => {
    if (step === 1) return form.org_name && form.responsible_name && form.phone && form.email;
    if (step === 2) return form.event_type;
    return true;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#060B14] flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-[#10B981]" />
          </div>
          <h2 className="text-2xl font-bold text-white">Demande envoyee !</h2>
          <p className="text-[#94A3B8]">Merci pour votre interet. Notre equipe va etudier votre demande et vous contacter rapidement pour activer votre espace.</p>
          <p className="text-sm text-[#64748B]">Vous recevrez un email de confirmation a <span className="text-[#0099CC]">{form.email}</span></p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#0099CC] hover:underline mt-4">Retour a l'accueil</Link>
        </div>
      </div>
    );
  }

  if (showSignup) {
    return (
      <div className="min-h-screen bg-[#060B14] py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <button className="text-[#0099CC] text-sm hover:underline mb-4 inline-block" onClick={() => setShowSignup(false)}>&larr; Retour</button>
            <h1 className="text-2xl font-bold text-white">Demander un acces gratuit</h1>
            <p className="text-[#94A3B8] mt-2">Remplissez les informations pour creer votre espace evenementiel</p>
            <p className="mt-3">
              <Link to="/login" className="text-sm text-[#64748b] hover:text-[#0099CC]" data-testid="signup-form-login-link">
                Deja un compte ? <span className="text-[#0099CC] font-medium underline underline-offset-2">Se connecter</span>
              </Link>
            </p>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="signup-steps">
            {STEPS_DATA.map((s, i) => (
              <div key={s.num} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step >= s.num ? "bg-[#0099CC] text-white" : "bg-[#1A2332] text-[#64748B]"}`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`text-xs hidden sm:inline ${step >= s.num ? "text-[#0099CC]" : "text-[#64748B]"}`}>{s.title}</span>
                {i < STEPS_DATA.length - 1 && <ChevronRight className="w-4 h-4 text-[#2A3441]" />}
              </div>
            ))}
          </div>

          {/* Form card */}
          <div className="bg-[#0B1120] border border-[#1A2332] rounded-2xl p-6 sm:p-8 space-y-5" data-testid="signup-form">
            {step === 1 && (
              <>
                <h3 className="text-lg font-semibold text-white mb-4">Votre organisation</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><label className="text-sm text-[#94A3B8] mb-1 block">Nom de l'organisation *</label><Input value={form.org_name} onChange={e => set("org_name", e.target.value)} placeholder="Ex: Association Diaspora" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="org-name-input" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Responsable *</label><Input value={form.responsible_name} onChange={e => set("responsible_name", e.target.value)} placeholder="Nom et prenom" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="responsible-input" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Telephone *</label><Input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+33 6 12 34 56 78" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="phone-input" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">E-mail *</label><Input value={form.email} onChange={e => set("email", e.target.value)} type="email" placeholder="contact@organisation.fr" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="email-input" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Site internet</label><Input value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://..." className="bg-[#060B14] border-[#1A2332] text-white" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Pays</label><Input value={form.country} onChange={e => set("country", e.target.value)} placeholder="France" className="bg-[#060B14] border-[#1A2332] text-white" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Ville</label><Input value={form.city} onChange={e => set("city", e.target.value)} placeholder="Paris" className="bg-[#060B14] border-[#1A2332] text-white" /></div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <h3 className="text-lg font-semibold text-white mb-4">Votre besoin</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="text-sm text-[#94A3B8] mb-1 block">Type d'evenement *</label>
                    <select value={form.event_type} onChange={e => set("event_type", e.target.value)} className="w-full bg-[#060B14] border border-[#1A2332] text-white rounded-md px-3 py-2" data-testid="event-type-select">
                      <option value="">Selectionnez</option>
                      <option value="conference">Conference / Seminaire</option>
                      <option value="gala">Gala / Soiree</option>
                      <option value="forum">Forum / Salon</option>
                      <option value="mariage">Mariage</option>
                      <option value="concert">Concert / Spectacle</option>
                      <option value="formation">Formation / Atelier</option>
                      <option value="corporate">Evenement corporate</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Participants estimes</label><Input value={form.estimated_participants} onChange={e => set("estimated_participants", e.target.value)} type="number" placeholder="500" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="participants-input" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Date prevue</label><Input value={form.planned_date} onChange={e => set("planned_date", e.target.value)} type="date" className="bg-[#060B14] border-[#1A2332] text-white" /></div>
                  <div className="sm:col-span-2">
                    <label className="text-sm text-[#94A3B8] mb-1 block">Frequence</label>
                    <select value={form.event_frequency} onChange={e => set("event_frequency", e.target.value)} className="w-full bg-[#060B14] border border-[#1A2332] text-white rounded-md px-3 py-2">
                      <option value="">Selectionnez</option>
                      <option value="unique">Unique</option>
                      <option value="mensuel">Mensuel</option>
                      <option value="trimestriel">Trimestriel</option>
                      <option value="annuel">Annuel</option>
                      <option value="regulier">Regulier</option>
                    </select>
                  </div>
                </div>
                <div className="pt-4">
                  <p className="text-sm text-[#94A3B8] mb-3">Services souhaites</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { key: "need_qrcode", label: "QR Code & inscription" },
                      { key: "need_access_control", label: "Controle d'acces" },
                      { key: "need_badges", label: "Badges nominatifs" },
                      { key: "need_audiovisual", label: "Audiovisuel" },
                      { key: "need_photo_video", label: "Reportage photo/video" },
                    ].map(n => (
                      <label key={n.key} className="flex items-center gap-2 text-sm text-white cursor-pointer hover:text-[#0099CC] transition-colors">
                        <input type="checkbox" checked={form[n.key]} onChange={e => set(n.key, e.target.checked)} className="rounded accent-[#0099CC]" />
                        {n.label}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <h3 className="text-lg font-semibold text-white mb-4">Personnalisation</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Couleur principale</label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={form.primary_color} onChange={e => set("primary_color", e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0" />
                        <span className="text-sm text-[#64748B]">{form.primary_color}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Couleur secondaire</label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={form.secondary_color} onChange={e => set("secondary_color", e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0" />
                        <span className="text-sm text-[#64748B]">{form.secondary_color}</span>
                      </div>
                    </div>
                  </div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Nom de votre plateforme</label><Input value={form.platform_name} onChange={e => set("platform_name", e.target.value)} placeholder="Ex: Diaspora Connect" className="bg-[#060B14] border-[#1A2332] text-white" /></div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">Informations complementaires</label><Textarea value={form.additional_info} onChange={e => set("additional_info", e.target.value)} placeholder="Decrivez votre besoin..." className="bg-[#060B14] border-[#1A2332] text-white min-h-[100px]" /></div>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <h3 className="text-lg font-semibold text-white mb-4">Recapitulatif de votre demande</h3>
                <div className="space-y-4">
                  <div className="bg-[#060B14] rounded-xl p-4 space-y-2 border border-[#1A2332]">
                    <h4 className="text-sm font-semibold text-[#0099CC]">Organisation</h4>
                    <p className="text-white">{form.org_name}</p>
                    <p className="text-sm text-[#94A3B8]">{form.responsible_name} — {form.email}</p>
                    <p className="text-sm text-[#94A3B8]">{form.phone} — {form.city}, {form.country}</p>
                  </div>
                  <div className="bg-[#060B14] rounded-xl p-4 space-y-2 border border-[#1A2332]">
                    <h4 className="text-sm font-semibold text-[#0099CC]">Evenement</h4>
                    <p className="text-white capitalize">{form.event_type || "Non precise"}</p>
                    <p className="text-sm text-[#94A3B8]">{form.estimated_participants || "?"} participants — {form.planned_date || "Date a definir"}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {form.need_qrcode && <span className="text-xs px-2 py-1 rounded-full bg-[#0099CC]/10 text-[#0099CC]">QR Code</span>}
                      {form.need_access_control && <span className="text-xs px-2 py-1 rounded-full bg-[#10B981]/10 text-[#10B981]">Controle d'acces</span>}
                      {form.need_badges && <span className="text-xs px-2 py-1 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6]">Badges</span>}
                      {form.need_audiovisual && <span className="text-xs px-2 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B]">Audiovisuel</span>}
                      {form.need_photo_video && <span className="text-xs px-2 py-1 rounded-full bg-[#EF4444]/10 text-[#EF4444]">Photo/Video</span>}
                    </div>
                  </div>
                  {form.platform_name && (
                    <div className="bg-[#060B14] rounded-xl p-4 space-y-2 border border-[#1A2332]">
                      <h4 className="text-sm font-semibold text-[#0099CC]">Personnalisation</h4>
                      <p className="text-white">{form.platform_name}</p>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded" style={{ backgroundColor: form.primary_color }} />
                        <div className="w-5 h-5 rounded" style={{ backgroundColor: form.secondary_color }} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t border-[#1A2332]">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(s => s - 1)} className="border-[#2A3441] text-white hover:bg-[#1A2332]">Retour</Button>
              ) : <div />}
              {step < 4 ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="next-step-btn">
                  Suivant <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading} className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="submit-signup-btn">
                  {loading ? "Envoi..." : "Envoyer ma demande"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Landing page
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0099CC]/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-xs font-semibold mb-6">
              <Zap className="h-3.5 w-3.5" /> Acces gratuit pendant le lancement
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 text-white">
              <span className="text-[#0099CC]">GLOTECH EVENT</span><br />
              Gerez vos inscriptions et vos participants simplement
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-2xl">
              Creez votre evenement, collectez les inscriptions, generez automatiquement les QR Codes et controlez les entrees depuis un smartphone.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Button onClick={() => setShowSignup(true)} className="bg-[#10B981] hover:bg-[#0d9668] text-white px-7 py-3.5 rounded-xl font-semibold text-base h-auto" data-testid="cta-signup">
                Demander un acces gratuit <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <a href="#features" className="inline-flex items-center gap-2 border border-[#2A3441] text-white hover:border-[#94A3B8] px-7 py-3.5 rounded-xl font-semibold transition-colors" data-testid="cta-discover">
                Decouvrir la plateforme
              </a>
            </div>
            <div className="mt-4">
              <Link to="/login" className="text-sm text-[#64748b] hover:text-[#0099CC] transition-colors" data-testid="already-account-link">
                Deja un compte ? <span className="text-[#0099CC] font-medium underline underline-offset-2">Se connecter</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-[#1A2332] bg-[#0B1120]/50">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { val: "Illimite", label: "Evenements" },
            { val: "5 000", label: "Participants / evt" },
            { val: "< 1s", label: "Scan QR Code" },
            { val: "100%", label: "Gratuit au lancement" },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-[#0099CC]">{s.val}</div>
              <div className="text-sm text-[#94A3B8] mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Tout ce dont vous avez besoin</h2>
            <p className="text-[#94A3B8]">Une plateforme complete pour gerer vos evenements de A a Z</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-[#0B1120] border border-[#1A2332] rounded-2xl p-6 hover:border-[#0099CC]/30 transition-all group">
                <div className="w-12 h-12 rounded-xl bg-[#0099CC]/10 flex items-center justify-center mb-4 group-hover:bg-[#0099CC]/20 transition-colors">
                  <f.icon className="w-6 h-6 text-[#0099CC]" />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-[#0B1120]/50 border-y border-[#1A2332]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-16">Comment ca marche ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Demandez un acces", desc: "Remplissez le formulaire. Notre equipe etudie votre demande sous 24 a 48 heures." },
              { step: "2", title: "Creez votre evenement", desc: "Personnalisez le formulaire d'inscription et publiez le lien." },
              { step: "3", title: "Collectez les inscriptions", desc: "Les participants s'inscrivent et recoivent leur QR Code par email." },
              { step: "4", title: "Controlez les entrees", desc: "Scannez les QR Codes a l'entree avec un simple smartphone." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#0099CC] text-white flex items-center justify-center text-lg font-bold mx-auto mb-4">{s.step}</div>
                <h3 className="text-base font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-[#94A3B8]">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button onClick={() => setShowSignup(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white px-8 py-3.5 rounded-xl font-semibold text-base h-auto" data-testid="cta-bottom">
              Commencer gratuitement <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Globe className="w-12 h-12 text-[#0099CC] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Pret a simplifier la gestion de vos evenements ?</h2>
          <p className="text-[#94A3B8] mb-8">Rejoignez les organisations qui font confiance a GLOTECH EVENT pour gerer leurs inscriptions et leur controle d'acces.</p>
          <Button onClick={() => setShowSignup(true)} className="bg-[#10B981] hover:bg-[#0d9668] text-white px-8 py-4 rounded-xl font-semibold text-base h-auto">
            Demander un acces gratuit <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <p className="mt-4">
            <Link to="/login" className="text-sm text-[#64748b] hover:text-[#0099CC]">
              Deja un compte ? <span className="text-[#0099CC] font-medium underline underline-offset-2">Se connecter</span>
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
